import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Outlet, useNavigate, useLocation, Link } from "react-router";
import { Breadcrumbs as FoodsBreadcrumbs, FoodsIcon, FoodsTabs, FoodsTabTemplateOption } from "@foodpilot/foods";
import { Loading } from "src/components/utils/Loading";
import { useTranslation } from "react-i18next";
import { URLHandler } from "src/services/URLHandler";
import { useSectionResolverContext } from "../SectionResolver";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { ActiveOrganizationContextHeader } from "src/components/ActiveOrganizationContextHeader";
import { PRODUCT, PRODUCT_ACTIVITY_DATA, useFoodpilotContext, VIEW, VIEW_SSQ } from "src/context/FoodpilotContext";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { Breadcrumbs as ResponsiveBreadcrumbs, type BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";

type SectionKeys = "scores" | "questionnaire" | "informations" | "value-chain" | string;
export type Sections = {
    section: SectionKeys;
};

export const ProductDetails = () => {
    const { section } = useSectionResolverContext();
    const { canUser, selectedCompany } = useFoodpilotContext();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { company } = useCompanyMetadata();
    const { product, ssqVersion, areProductCompaniesVisible, activeCompanyId } = useProductMetadataContext();
    const { applicationParameters } = useApplicationParameters();
    const isValueChainEnabled = applicationParameters?.["FS-product-value-chain"] === "true";
    const isProductUxV2RolloutEnabled = useIsProductUxV2();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const canViewActivityData = canUser(VIEW, PRODUCT_ACTIVITY_DATA);

    const searchParams = new URLSearchParams(location.search);

    if (!section || !selectedCompany) return <Loading />;

    const isIntermediateProduct = product.isIntermediate === true;
    const isProductUxV2 = isProductUxV2RolloutEnabled;
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;

    const tabs: FoodsTabTemplateOption<SectionKeys>[] = [];

    const _returnUrl = searchParams.get("returnUrl");
    const returnUrl = _returnUrl ? `returnUrl=${encodeURIComponent(_returnUrl)}` : "";

    const productListUrl = urlHandlerRoutes.list(company.id);
    const productListLabel =
        isIntermediateProduct ?
            t("sidebar.intermediate_products", "Intermediate products")
        :   t("sidebar.finished_products", "All finished products");
    const breadcrumbItems: BreadcrumbType[] = [
        {
            label: productListLabel,
            action: () => navigate(productListUrl),
        },
        { label: product.title },
    ];

    const _scoreUrl = urlHandlerRoutes.scores(company.id, product.id);
    const scoreUrl = `${_scoreUrl}?${returnUrl}`;
    tabs.push({
        id: "scores",
        name: (
            <Link
                to={scoreUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <Box sx={{ display: "flex", paddingBottom: theme.spacing(0.25) }}>
                        <FoodsIcon size={2} icon="scoresIndicators" />
                    </Box>
                    <Typography variant="body-bold">{t("scores_indicateurs_cles")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                navigate(scoreUrl);
            },
        },
    });

    if (canUser(VIEW_SSQ, PRODUCT) && ssqVersion !== undefined) {
        const _qssUrl = urlHandlerRoutes.ssqProduct(company.id, product.id, activeCompanyId);
        const qssUrl = `${_qssUrl}?${returnUrl}`;
        tabs.push({
            id: "questionnaire",
            name: (
                <Link
                    to={qssUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box sx={{ display: "flex", paddingBottom: theme.spacing(0.25) }}>
                            <FoodsIcon size={2} icon="questionnaires" />
                        </Box>
                        <Typography variant="body-bold">{t("questionnaire")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(qssUrl);
                },
            },
        });
    }

    if (!isProductUxV2 && areProductCompaniesVisible && canViewActivityData) {
        const activitiesUrl = URLHandler.product.activity(selectedCompany.id, product.id);
        tabs.push({
            id: "activities",
            name: (
                <Link
                    to={activitiesUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box sx={{ display: "flex", paddingBottom: theme.spacing(0.25) }}>
                            <FoodsIcon size={2} icon="activityData" />
                        </Box>
                        <Typography variant="body-bold">{t("donnees_d_activite")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(activitiesUrl);
                },
            },
        });
    }

    const informationUrl = urlHandlerRoutes.information(company.id, product.id);
    tabs.push({
        id: "informations",
        name: (
            <Link
                to={informationUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <Box sx={{ display: "flex", paddingBottom: theme.spacing(0.25) }}>
                        <FoodsIcon size={2} icon="informations" />
                    </Box>
                    <Typography variant="body-bold">{t("informations")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                navigate(informationUrl);
            },
        },
    });

    if (!isProductUxV2 && isValueChainEnabled) {
        const valueChainUrl = urlHandlerRoutes.valueChain(company.id, product.id);
        tabs.push({
            id: "value-chain",
            name: (
                <Link
                    to={valueChainUrl}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                        <Box sx={{ display: "flex", paddingBottom: theme.spacing(0.25) }}>
                            <FoodsIcon size={3} icon="graphLink" />
                        </Box>
                        <Typography variant="body-bold">{t("value_chain", "Chaîne de valeur")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(valueChainUrl);
                },
            },
        });
    }

    const selectedTab = tabs.findIndex((i) => i.id === section);
    const selectedTabIndex = selectedTab >= 0 ? selectedTab : 0;

    return (
        <Box
            component="div"
            data-testid={isProductUxV2 ? "product-v2-details-shell" : undefined}
            sx={{
                marginInlineEnd: "auto",
                marginInlineStart:
                    isProductUxV2 && !isMobile ?
                        `calc(var(--app-navigation-overlap, ${theme.spacing(2.5)}) * -1)`
                    :   "auto",
            }}
        >
            {isProductUxV2 ?
                <Box
                    data-testid="product-v2-details-header"
                    sx={{
                        backgroundColor: theme.custom.grey[100],
                        position: "relative",
                    }}
                >
                    <Box sx={{ paddingInline: theme.spacing(6), paddingTop: theme.spacing(3) }}>
                        {isMobile ?
                            <ResponsiveBreadcrumbs items={breadcrumbItems} />
                        :   <FoodsBreadcrumbs
                                ariaLabel={t("common.navigation.breadcrumb", "Breadcrumb")}
                                ancestors={[
                                    {
                                        id: "product-details-list",
                                        label: breadcrumbItems[0].label,
                                        onSelect: breadcrumbItems[0].action!,
                                    },
                                ]}
                                current={{ id: "product-details-current", label: product.title }}
                            />
                        }
                    </Box>
                    <Box sx={{ marginTop: theme.spacing(1), paddingInline: theme.spacing(6) }}>
                        <FoodsTabs
                            forceScrollable
                            tabs={tabs}
                            selectedTabIndex={selectedTabIndex}
                            tabVariant="primary"
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            borderBottomColor: theme.custom.strokes.containerDivider.color,
                            borderBottomStyle: theme.custom.strokes.containerDivider.style,
                            borderBottomWidth: theme.custom.strokes.containerDivider.width,
                            bottom: 0,
                            left: 0,
                            pointerEvents: "none",
                            position: "absolute",
                            right: 0,
                        }}
                    />
                </Box>
            :   <Box
                    sx={{
                        width: "auto",
                        background: theme.custom.grey[100],
                        padding: theme.spacing(3.5, 6.5, 0),
                        position: "relative",
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={theme.spacing(2)}>
                        <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                            <ArrowBackIcon
                                sx={{ cursor: "pointer", margin: theme.spacing(1) }}
                                onClick={() => navigate(productListUrl)}
                            />
                            <ActiveOrganizationContextHeader
                                title={<Typography variant="h1">{product.title}</Typography>}
                            />
                        </Box>
                    </Stack>

                    <FoodsTabs forceScrollable tabs={tabs} selectedTabIndex={selectedTabIndex} tabVariant="primary" />
                </Box>
            }

            <Stack
                data-testid={isProductUxV2 ? "product-v2-details-content" : undefined}
                padding={theme.spacing(0, 6)}
                sx={{
                    backgroundColor: isProductUxV2 ? theme.custom.grey[300] : undefined,
                    minHeight: "100%",
                }}
            >
                <Outlet />
            </Stack>
        </Box>
    );
};
